import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { ScanAlert } from '../hooks/useScanAlerts';

export interface ScanAlertListModalProps {
  open: boolean;
  alerts: ScanAlert[];
  onClose: () => void;
  onDismiss: (id: string) => void;
  onClearAll: () => void;
  /** Satırdaki "Hızlı Stok Kaydı" — okutulan barkodla stok kartı açar. */
  onQuickAdd: (term: string) => void;
  /** Hızlı stok kaydı yetkisi yoksa buton gösterilmez. */
  canQuickAddItem: boolean;
}

const formatTime = (at: number) =>
  new Date(at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * Sepetteki uyarı şeridine tıklandığında açılan detay listesi.
 *
 * Fare ile açıldığı için normal (odak hapisli) Dialog olmasında sakınca yok;
 * barkod okutma akışını kesen tek şey otomatik açılan modaldı, bu modal ise
 * yalnızca kasiyer isteyince açılıyor. Kapanışta odak arama kutusuna döner.
 */
const ScanAlertListModal: React.FC<ScanAlertListModalProps> = ({
  open,
  alerts,
  onClose,
  onDismiss,
  onClearAll,
  onQuickAdd,
  canQuickAddItem,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        Sepete Eklenemeyen Okutmalar
        <Chip size="small" label={alerts.length} color="warning" sx={{ ml: 'auto' }} />
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {alerts.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            Bekleyen uyarı yok.
          </Typography>
        ) : (
          <List dense disablePadding>
            {alerts.map((alert, index) => (
              <React.Fragment key={alert.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem sx={{ alignItems: 'flex-start', gap: 1, py: 1.25 }}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '1rem' }}>
                        {alert.term}
                      </Typography>
                      <Chip size="small" label={alert.title} color="warning" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(alert.at)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'pre-line', mt: 0.5 }}
                    >
                      {alert.message}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {alert.canQuickAdd && canQuickAddItem && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlaylistAddIcon />}
                        onClick={() => onQuickAdd(alert.term)}
                      >
                        Hızlı Stok Kaydı
                      </Button>
                    )}
                    <Tooltip title="Bu uyarıyı sil">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={`${alert.term} uyarısını sil`}
                        onClick={() => onDismiss(alert.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button color="error" onClick={onClearAll} disabled={alerts.length === 0}>
          Tümünü Temizle
        </Button>
        <Button variant="contained" onClick={onClose}>
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScanAlertListModal;
